import React, { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import { useSnackbar } from 'notistack'
import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns'
import PageTitle from '../../components/PageTitle'
import {
  useGetFreightPnlSummaryQuery,
  useRecomputeFreightPnlMutation,
  useGetFreightPnlRecomputeStatusQuery,
} from '../../redux/freightPnlApiSlice'
import FreightPnlFilters from './FreightPnlFilters'
import KpiTile from './KpiTile'
import PnlTable from './PnlTable'
import OrdersTab from './OrdersTab'
import BreakdownPanels from './BreakdownPanels'
import MarginTargetsTab from './MarginTargetsTab'
import {
  count, dayStr, downloadRowsAsCsv, exportErrorMessage, exportFreightOrdersCsv, inr, inr2, pct2, share, signedInr, unpricedStatement,
} from './freightPnlUtils'

const DEFAULT_LOSS_THRESHOLD = 10
// Backend caps one recompute request at this many days (~2-5 s per day per segment on prod).
const MAX_RECOMPUTE_DAYS = 31

// `change: true` columns compare with the previous period and are hidden when that period is not fully computed.
const BRAND_COLS = [
  { key: 'merchantName', label: 'Merchant', format: 'text' },
  { key: 'totalShipments', label: 'Shipments', format: 'count' },
  { key: 'pricedShipments', label: 'Priced', format: 'count' },
  { key: 'ordersWithoutPricing', label: 'Rates not calculated', format: 'count' },
  { key: 'lossShipments', label: 'Loss AWBs', format: 'count' },
  { key: 'lossAmount', label: 'Gross loss ₹', format: 'inr' },
  { key: 'grossProfitLoss', label: 'Gross P/L ₹', format: 'inr' },
  { key: 'percentageProfitLoss', label: '% P/L', format: 'pct' },
  { key: 'cm2', label: 'CM2 incl. RTO ₹', format: 'inr' },
  { key: 'cm2Change', label: 'CM2 change ₹', format: 'signedInr', change: true },
]
const COURIER_COLS = [
  { key: 'courierName', label: 'Courier code', format: 'text' },
  { key: 'courierAccount', label: 'Account', format: 'text' },
  { key: 'courierFamily', label: 'Family', format: 'text' },
  { key: 'totalShipments', label: 'Shipments', format: 'count' },
  { key: 'pricedShipments', label: 'Priced', format: 'count' },
  { key: 'ordersWithoutPricing', label: 'Rates not calculated', format: 'count' },
  { key: 'lossShipments', label: 'Loss AWBs', format: 'count' },
  { key: 'lossAmount', label: 'Gross loss ₹', format: 'inr' },
  { key: 'grossProfitLoss', label: 'Gross P/L ₹', format: 'inr' },
  { key: 'percentageProfitLoss', label: '% P/L', format: 'pct' },
  { key: 'cm2', label: 'CM2 incl. RTO ₹', format: 'inr' },
  { key: 'cm2Change', label: 'CM2 change ₹', format: 'signedInr', change: true },
]
const FAMILY_COLS = [
  { key: 'name', label: 'Courier family', format: 'text' },
  { key: 'pricedShipments', label: 'Priced', format: 'count' },
  { key: 'revenue', label: 'Revenue ₹', format: 'inr' },
  { key: 'revenueChangePct', label: 'Revenue change', format: 'pct2', change: true },
  { key: 'cm2', label: 'CM2 ₹', format: 'inr' },
  { key: 'cm2Change', label: 'CM2 change ₹', format: 'signedInr', change: true },
  { key: 'cm2Pct', label: 'CM2 %', format: 'pct2' },
]
const ZONE_COLS = [
  { key: 'name', label: 'Merchant zone', format: 'text' },
  { key: 'pricedShipments', label: 'Priced', format: 'count' },
  { key: 'revenue', label: 'Revenue ₹', format: 'inr' },
  { key: 'cm2', label: 'CM2 ₹', format: 'inr' },
  { key: 'cm2Change', label: 'CM2 change ₹', format: 'signedInr', change: true },
  { key: 'cm2Pct', label: 'CM2 %', format: 'pct2' },
  { key: 'cm2PctChange', label: 'CM2 % change (pp)', format: 'pct2', change: true },
]
const LANE_COLS = [
  { key: 'merchantName', label: 'Client', format: 'text' },
  { key: 'courierName', label: 'Courier code', format: 'text' },
  { key: 'courierAccount', label: 'Account', format: 'text' },
  { key: 'pricedShipments', label: 'Priced', format: 'count' },
  { key: 'revenue', label: 'Revenue ₹', format: 'inr' },
  { key: 'cm2', label: 'CM2 ₹', format: 'inr' },
  { key: 'cm2Change', label: 'CM2 change ₹', format: 'signedInr', change: true },
  { key: 'cm2Pct', label: 'CM2 %', format: 'pct2' },
]

const TAB_BRAND = 0
const TAB_COURIER = 1
const TAB_FAMILY = 2
const TAB_ZONE = 3
const TAB_LANES = 4
const TAB_UNPRICED = 5
const TAB_ORDERS = 6
const TAB_MARGIN = 7

/** A change line for a KPI tile; `higherIsBetter` picks the colour. Null when there is nothing to compare with. */
function changeOf(cur, prev, enabled, higherIsBetter = true) {
  if (!enabled || cur === undefined || prev === undefined) return null
  const d = Math.round(cur - prev)
  return { text: `${signedInr(d)} vs previous`, tone: d === 0 ? 'flat' : (d > 0) === higherIsBetter ? 'up' : 'down' }
}

/**
 * Freight Profitability — every figure the daily, weekly and loss-making profitability mails print, for any period.
 * The page and the mails read the same rollup rows: cohort = AWB-registered shipments in the IST day(s), sell = merchant
 * rate-card price, buy = courier rate-card price, both excluding GST; shipments missing either price are "rates not
 * calculated" and sit outside every profit figure. Gross P/L is the forward leg (daily mail); CM2 adds the separately
 * priced RTO leg (weekly mail). Backend: /api/finance/freight-profitability.
 */
export default function FreightProfitability() {
  const { enqueueSnackbar } = useSnackbar()
  const yesterday = dayStr(subDays(new Date(), 1))
  const [filters, setFilters] = useState({
    segment: 'D2C', from: yesterday, to: yesterday, merchantOids: [],
    lossMakingOnly: false, lossThresholdPct: DEFAULT_LOSS_THRESHOLD,
  })
  const [tab, setTab] = useState(TAB_BRAND)

  // Scope sent to every endpoint; the loss-making threshold only travels when the toggle is on.
  const scope = useMemo(() => ({
    segment: filters.segment, from: filters.from, to: filters.to,
    merchantOids: filters.merchantOids,
    lossMakingOnly: filters.lossMakingOnly,
    lossThresholdPct: filters.lossMakingOnly && filters.lossThresholdPct !== '' ? filters.lossThresholdPct : undefined,
  }), [filters])

  const { data, isFetching, error, refetch } = useGetFreightPnlSummaryQuery(scope)
  const s = data?.summary || {}
  const p = data?.previous || {}
  const threshold = data?.lossThresholdPct ?? DEFAULT_LOSS_THRESHOLD
  // Changes are shown only when the previous period is fully computed; otherwise they would compare against a partial period.
  const withChange = Boolean(data?.previous) && (data?.previousMissingDays || []).length === 0

  // Derived change columns, computed once per response.
  const tables = useMemo(() => {
    const ch = (r) => ({ ...r, cm2Change: r.cm2 - (r.previousCm2 || 0) })
    return {
      [TAB_BRAND]: (data?.byBrand || []).map(ch),
      [TAB_COURIER]: (data?.byCourier || []).map(ch),
      // revenueChangePct / cm2PctChange come from the backend, rounded as the weekly mail rounds them.
      [TAB_FAMILY]: (data?.byFamily || []).map(ch),
      [TAB_ZONE]: (data?.byZone || []).map(ch),
      [TAB_LANES]: (data?.lossLanes || []).map(ch),
    }
  }, [data])
  const cols = (c) => (withChange ? c : c.filter(x => !x.change))
  const TABLE_TABS = {
    [TAB_BRAND]: { cols: BRAND_COLS, name: 'BY_BRAND' },
    [TAB_COURIER]: { cols: COURIER_COLS, name: 'BY_COURIER_CODE' },
    [TAB_FAMILY]: { cols: FAMILY_COLS, name: 'BY_COURIER_FAMILY' },
    [TAB_ZONE]: { cols: ZONE_COLS, name: 'BY_ZONE' },
    [TAB_LANES]: { cols: LANE_COLS, name: 'LOSS_LANES' },
  }

  // Order-level reads don't know about the loss-making rule; scope them to the brands the summary kept, so the
  // Order level / Rates not calculated tabs match the mail's loss-making CSVs. Totals come from the summary too.
  const ordersScope = useMemo(() => {
    if (!filters.lossMakingOnly) return scope
    return { ...scope, merchantOids: (data?.byBrand || []).map(b => b.merchantOid) }
  }, [scope, filters.lossMakingOnly, data])
  const ordersSkip = filters.lossMakingOnly && (!data || (data.byBrand || []).length === 0)

  // Recompute (backfill / refresh) — async on the backend; poll until done, then refetch.
  const [recompute, { isLoading: queueing }] = useRecomputeFreightPnlMutation()
  const [recomputeId, setRecomputeId] = useState(null)
  const { data: recomputeStatus, error: pollError } = useGetFreightPnlRecomputeStatusQuery(recomputeId, { skip: !recomputeId, pollingInterval: 3000 })
  // Stop polling after a run of failed polls instead of spinning forever (e.g. the job table is unreachable).
  const pollErrors = useRef(0)
  useEffect(() => {
    if (!recomputeId) { pollErrors.current = 0; return }
    if (pollError) {
      pollErrors.current += 1
      if (pollErrors.current >= 5) {
        setRecomputeId(null)
        enqueueSnackbar('Lost track of the recompute job. It may still be running — check FREIGHT_PNL_RECOMPUTE in the server logs and refresh in a minute.', { variant: 'warning', autoHideDuration: 10000 })
      }
    } else if (recomputeStatus) {
      pollErrors.current = 0
    }
  }, [pollError, recomputeStatus, recomputeId, enqueueSnackbar])
  useEffect(() => {
    if (!recomputeStatus) return
    if (recomputeStatus.state === 'DONE' || recomputeStatus.state === 'FAILED') {
      setRecomputeId(null)
      // results = { 'yyyy-MM-dd': { D2C: rowsWritten, PTL: rowsWritten } }; -1 marks a day/segment that threw.
      const failed = Object.entries(recomputeStatus.results || {})
        .filter(([, seg]) => Object.values(seg || {}).some(v => v < 0))
        .map(([day]) => day)
      if (recomputeStatus.state === 'FAILED') {
        enqueueSnackbar(`Recompute failed: ${recomputeStatus.error || 'see server logs'}`, { variant: 'error' })
      } else if (failed.length) {
        enqueueSnackbar(`Recomputed ${recomputeStatus.daysDone} day(s); failed for ${failed.join(', ')} — see FREIGHT_PNL_RECOMPUTE in server logs`, { variant: 'warning', autoHideDuration: 10000 })
      } else {
        enqueueSnackbar(`Recomputed ${recomputeStatus.daysDone} day(s)`, { variant: 'success' })
      }
      refetch()
    }
  }, [recomputeStatus, enqueueSnackbar, refetch])

  const onRecompute = async () => {
    try {
      const res = await recompute({ from: filters.from, to: filters.to, segment: filters.segment }).unwrap()
      setRecomputeId(res.requestId)
      enqueueSnackbar(`Recompute queued for ${res.daysTotal} day(s)`, { variant: 'info' })
    } catch (e) {
      // 409 carries the running job's range and progress; 400 the 31-day cap or the archive-age limit.
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not queue recompute', { variant: 'error', autoHideDuration: 8000 })
    }
  }

  const recomputing = queueing || Boolean(recomputeId)
  // parseISO reads 'yyyy-MM-dd' as local midnight; new Date('yyyy-MM-dd') is UTC midnight and shifts a day west of UTC.
  const rangeDays = differenceInCalendarDays(parseISO(filters.to), parseISO(filters.from)) + 1
  const rangeTooWide = rangeDays > MAX_RECOMPUTE_DAYS
  const recomputeTitle = recomputing ? 'A recompute is already running'
    : rangeTooWide ? `Recompute covers at most ${MAX_RECOMPUTE_DAYS} days at a time — narrow the date range`
    : `Recompute ${rangeDays} day(s) for ${filters.segment} from Mongo orders`
  // No priced shipment → nothing to colour; 0 is "no data", not "break-even".
  const grossTone = (v) => (v === undefined || v === null || !s.pricedShipments ? 'neutral' : Number(v) < 0 ? 'loss' : 'profit')
  const unpricedShare = s.totalShipments ? Math.round((s.ordersWithoutPricing / s.totalShipments) * 100) : 0
  const fmtRange = (from, to) => (from === to
    ? format(parseISO(from), 'dd MMMM yyyy')
    : `${format(parseISO(from), 'dd MMM')} – ${format(parseISO(to), 'dd MMM yyyy')}`)
  const periodLabel = fmtRange(filters.from, filters.to)
  const previousLabel = data?.previousFrom ? fmtRange(data.previousFrom, data.previousTo) : null

  // QA / mail parity: whenever the scope has orders without rates, say so in the mail's own words — as a banner, and as a
  // toast after every successful download (the mail carries this sentence above its attachments).
  const statement = unpricedStatement(s.ordersWithoutPricing)
  const [downloadingUnpriced, setDownloadingUnpriced] = useState(false)
  const downloadUnpriced = async () => {
    setDownloadingUnpriced(true)
    try {
      await exportFreightOrdersCsv(ordersScope, false)
    } catch (e) {
      enqueueSnackbar(exportErrorMessage(e), { variant: 'error' })
    } finally {
      setDownloadingUnpriced(false)
    }
  }
  const afterDownload = (pricedExport) => {
    // The unpriced report itself needs no reminder about itself.
    if (!statement || pricedExport === false) return
    enqueueSnackbar(statement, {
      variant: 'warning', autoHideDuration: 12000,
      action: <Button size='small' color='inherit' onClick={downloadUnpriced} sx={{ textTransform: 'none', fontWeight: 700 }}>Download</Button>,
    })
  }

  const exportTable = () => {
    const t = TABLE_TABS[tab]
    if (!t) return
    downloadRowsAsCsv(
      tables[tab] || [],
      cols(t.cols),
      `PROSHIP_FREIGHT_PNL_${t.name}_${filters.segment}_${filters.from}${filters.from === filters.to ? '' : '_' + filters.to}.csv`,
    )
    afterDownload(true)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageTitle>Freight Profitability</PageTitle>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700 }}>Freight Profitability</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 900 }}>
            Sell (charged to brands) vs buy (charged by couriers) on shipments AWB-registered in the period, excluding GST — the same
            figures as the daily and weekly profitability mails. Gross P/L is the forward leg; CM2 adds the separately priced RTO leg.
            Excludes test and external merchants, external couriers and inter-warehouse transfers. Does not factor in weight
            discrepancy, lost shipments or later finance corrections.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {data?.computedAt && (
            <Typography variant='caption' sx={{ color: 'text.disabled' }}>
              Computed {format(new Date(data.computedAt), 'dd MMM HH:mm')} IST
            </Typography>
          )}
          <span title={recomputeTitle}>
            <Button size='small' variant='outlined' startIcon={<RefreshOutlinedIcon />} onClick={onRecompute} disabled={recomputing || rangeTooWide} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {recomputing ? 'Recomputing…' : 'Recompute period'}
            </Button>
          </span>
        </Box>
      </Box>

      <FreightPnlFilters filters={filters} onChange={(f) => { setFilters(f) }} disabled={recomputing} />

      {error && <Alert severity='error'>{error?.data?.message || error?.message || 'Could not load the report.'}</Alert>}

      {!isFetching && data?.missingDays?.length > 0 && (
        <Alert severity='warning' action={<Button size='small' color='inherit' onClick={onRecompute} disabled={recomputing || rangeTooWide} title={recomputeTitle}>Recompute</Button>}>
          No current figures yet for {data.missingDays.length === 1 ? data.missingDays[0] : `${data.missingDays.length} day(s): ${data.missingDays.join(', ')}`} (not
          computed, or computed before the figures excluded GST). Figures below cover only the other days.
        </Alert>
      )}

      {filters.lossMakingOnly && !isFetching && data && (
        <Alert severity='error' icon={false} sx={{ bgcolor: 'error.lighter' }}>
          <strong>{count(data.byBrand?.length)} of {count(data.totalBrands)} brands</strong> are below {threshold}% profit for {periodLabel}.
          Totals below cover only these brands, and every table is recomputed from their shipments — this is the "Loss Making Clients" mail.
        </Alert>
      )}

      {/* Brief Summary — the daily mail's numbers plus the weekly mail's CM2 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.5 }}>
        <KpiTile title='Shipments handled' value={count(s.totalShipments)} sub={`${count(s.pricedShipments)} priced · ${count(s.ordersWithoutPricing)} unpriced`} loading={isFetching} />
        <KpiTile title='Buy · couriers' value={inr(s.buyPrice)} sub='charged by couriers, excl. GST' loading={isFetching} change={changeOf(s.buyPrice, p.buyPrice, withChange, false)} />
        <KpiTile title='Sell · brands' value={inr(s.sellPrice)} sub='charged to brands, excl. GST' loading={isFetching} change={changeOf(s.sellPrice, p.sellPrice, withChange)} />
        <KpiTile title='Gross profit / loss' value={inr(s.grossProfitLoss)} sub={`${pct2(s.grossPct)} of sell`} tone={grossTone(s.grossProfitLoss)} loading={isFetching} change={changeOf(s.grossProfitLoss, p.grossProfitLoss, withChange)} />
        <KpiTile title='Gross profit / shipment' value={inr2(s.grossPerShipment)} sub='per priced shipment' tone={grossTone(s.grossPerShipment)} loading={isFetching} />
        <KpiTile title='Loss-making AWBs' value={count(s.lossShipments)} sub={`${pct2(share(s.lossShipments, s.pricedShipments))} of priced · gross loss ${inr(s.lossAmount)}`} tone={s.lossShipments ? 'loss' : 'neutral'} loading={isFetching} />
        <KpiTile title='CM2 incl. RTO' value={inr(s.cm2)} sub={`${pct2(s.cm2Pct)} of revenue ${inr(s.revenue)} · RTO leg ${inr((s.rtoSell || 0) - (s.rtoBuy || 0))}`} tone={grossTone(s.cm2)} loading={isFetching} change={changeOf(s.cm2, p.cm2, withChange)} />
        <KpiTile title='Rates not calculated' value={count(s.ordersWithoutPricing)} sub={`${unpricedShare}% of shipments · outside all P/L`} tone={s.ordersWithoutPricing ? 'warning' : 'neutral'} loading={isFetching} onClick={() => setTab(TAB_UNPRICED)} />
      </Box>

      {!isFetching && data && previousLabel && (
        <Typography variant='caption' sx={{ color: 'text.secondary', mt: -1 }}>
          {withChange
            ? `Changes compare with ${previousLabel}, the period of the same length just before.`
            : `Changes are hidden: ${previousLabel} (the period before) is not fully computed.`}
        </Typography>
      )}

      {!isFetching && data && statement && (
        <Alert
          severity='warning'
          action={
            <Button size='small' color='inherit' onClick={downloadUnpriced} disabled={downloadingUnpriced || ordersSkip} startIcon={<DownloadOutlinedIcon />} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              {downloadingUnpriced ? 'Preparing…' : 'Download report'}
            </Button>
          }
        >
          {statement}
        </Alert>
      )}

      {!isFetching && data && (s.reverseShipments > 0 || s.cancelledShipments > 0 || s.rtoShipments > 0) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {s.reverseShipments > 0 && <Chip size='small' variant='outlined' label={`Includes ${count(s.reverseShipments)} reverse (RVP) shipments`} />}
          {s.cancelledShipments > 0 && <Chip size='small' variant='outlined' label={`Includes ${count(s.cancelledShipments)} cancelled after AWB`} />}
          {s.rtoShipments > 0 && <Chip size='small' variant='outlined' label={`${count(s.rtoShipments)} in RTO so far`} />}
        </Box>
      )}

      <BreakdownPanels data={data} segment={filters.segment} loading={isFetching} withChange={withChange} />

      <Paper variant='outlined' sx={{ px: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant='scrollable' scrollButtons='auto' sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: 110 } }}>
            <Tab label={`By brand${data ? ` · ${count(data.byBrand?.length)}` : ''}`} />
            <Tab label={`By courier code${data ? ` · ${count(data.byCourier?.length)}` : ''}`} />
            <Tab label={`By courier family${data ? ` · ${count(data.byFamily?.length)}` : ''}`} />
            <Tab label={`By zone${data ? ` · ${count(data.byZone?.length)}` : ''}`} />
            <Tab label={`Loss lanes${data ? ` · ${count(data.lossLanes?.length)}` : ''}`} />
            <Tab label={`Rates not calculated${data ? ` · ${count(s.ordersWithoutPricing)}` : ''}`} />
            <Tab label={`Order level${data ? ` · ${count(s.pricedShipments)}` : ''}`} />
            <Tab label='Margin targets' />
          </Tabs>
          {TABLE_TABS[tab] && (
            <Button size='small' startIcon={<DownloadOutlinedIcon />} onClick={exportTable} disabled={isFetching || !data} sx={{ textTransform: 'none', fontWeight: 600 }}>CSV</Button>
          )}
        </Box>

        <Box sx={{ pt: 1.5 }}>
          {tab === TAB_BRAND && (
            <PnlTable
              rows={tables[TAB_BRAND]} columns={cols(BRAND_COLS)} loading={isFetching} lossThreshold={threshold}
              onRowClick={(row) => setFilters(f => ({ ...f, merchantOids: [row.merchantOid] }))}
              emptyText={filters.lossMakingOnly ? `No brand is below ${threshold}% for this period` : 'No priced shipments for this scope'}
            />
          )}
          {tab === TAB_COURIER && <PnlTable rows={tables[TAB_COURIER]} columns={cols(COURIER_COLS)} loading={isFetching} lossThreshold={threshold} />}
          {tab === TAB_FAMILY && (
            <PnlTable rows={tables[TAB_FAMILY]} columns={cols(FAMILY_COLS)} loading={isFetching} rowKey={r => r.name}
              defaultOrderBy={withChange ? 'cm2Change' : 'cm2'} defaultOrder='desc' />
          )}
          {tab === TAB_ZONE && (
            <PnlTable rows={tables[TAB_ZONE]} columns={cols(ZONE_COLS)} loading={isFetching} rowKey={r => r.name}
              defaultOrderBy={withChange ? 'cm2Change' : 'cm2'} defaultOrder='desc' />
          )}
          {tab === TAB_LANES && (
            <>
              <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                Client × courier code pairs with negative CM2, worst first. The weekly mail lists the ten worst with at least
                {` ${inr(data?.laneMinRevenue)}`} of weekly revenue.{data?.lossLanesTruncated ? ' Showing the worst 1,000.' : ''}
              </Typography>
              <PnlTable rows={tables[TAB_LANES]} columns={cols(LANE_COLS)} loading={isFetching} rowKey={r => `${r.merchantOid}|${r.courierOid}`}
                defaultOrderBy='cm2' defaultOrder='asc' emptyText='No lane lost money in this period' />
            </>
          )}
          {tab === TAB_UNPRICED && <OrdersTab scope={ordersScope} priced={false} total={s.ordersWithoutPricing || 0} skip={ordersSkip} onExported={afterDownload} />}
          {tab === TAB_ORDERS && <OrdersTab scope={ordersScope} priced total={s.pricedShipments || 0} skip={ordersSkip} onExported={afterDownload} />}
          {tab === TAB_MARGIN && <MarginTargetsTab />}
        </Box>
      </Paper>

      <Typography variant='caption' sx={{ color: 'text.disabled' }}>
        All rupee figures exclude GST and are whole rupees, as in the mails. "% P/L" = (sell − buy) ÷ sell, whole percent. Gross P/L and
        loss-making AWBs use the forward leg only (daily mail); CM2 adds the RTO leg's merchant charge to revenue and courier charge to cost
        (weekly mail). Brand and courier rows appear only with at least one priced shipment; unpriced shipments still count in "Shipments
        handled" and "Rates not calculated". Click a brand to scope the page to it.
      </Typography>
    </Box>
  )
}
